/**
 * Recognising the one delete refusal that is not an error (CWS-1384).
 *
 * svc-customer refuses to delete a vehicle that still carries an active membership,
 * because the subscription would go on billing for a car the lane can no longer
 * resolve. That refusal is a state the operator has to act on, not a failure to
 * report and forget, so the UI shows it in a modal with the cancel path rather than
 * a toast that slides away.
 */

/** Stable code on the 409 body. Matched instead of the sentence, which is free to change. */
export const VEHICLE_HAS_ACTIVE_MEMBERSHIP_CODE = 'VEHICLE_HAS_ACTIVE_MEMBERSHIP';

/** Fallback copy for the rare case the server sends the code with no message. */
export const VEHICLE_HAS_ACTIVE_MEMBERSHIP_FALLBACK =
  'Existing membership found for this vehicle. Cancel the membership from Orders > Actions > Cancel before deleting it.';

interface DeleteBlockBody {
  message?: string | null;
  data?: { code?: string | null } | null;
}

/**
 * Whether this HTTP failure is the active-membership refusal.
 *
 * Reads the code out of the error body wherever the caller hands it in: Angular wraps
 * the parsed body under `error`, but the same shape arrives bare from a non-2xx
 * response that a caller has already unwrapped.
 */
export function isActiveMembershipDeleteBlock(error: unknown): boolean {
  const body = extractBody(error);
  return body?.data?.code === VEHICLE_HAS_ACTIVE_MEMBERSHIP_CODE;
}

/** The server's own wording for the refusal, falling back to ours when it sends none. */
export function activeMembershipDeleteMessage(error: unknown): string {
  const message = extractBody(error)?.message;
  return typeof message === 'string' && message.trim()
    ? message.trim()
    : VEHICLE_HAS_ACTIVE_MEMBERSHIP_FALLBACK;
}

function extractBody(error: unknown): DeleteBlockBody | null {
  if (!error || typeof error !== 'object') {
    return null;
  }
  const wrapped = (error as { error?: unknown }).error;
  if (wrapped && typeof wrapped === 'object') {
    return wrapped as DeleteBlockBody;
  }
  return error as DeleteBlockBody;
}
